import React, { Suspense, lazy, useRef } from "react";
import { shallowEqual, useSelector } from "react-redux";
import {
  FILTERS,
  SORT_FILTER,
  HOUSING_FEST_FILTER,
  LOCALITY_FILTER,
  HIDE_LOCALITY_FROM_TOP_BAR,
} from "../../data/filtersList";
import { AppState } from "@Store/index";
import getFilterActiveStatus from "@Pages/Search/utils/getFilterActiveStatus";
import Badge, { FilterNameType } from "../Badge/Badge";
import FilterIcon from "@Icons/FilterIcon";
import getFilterLabel from "@Pages/Search/utils/getFilterLabel";
import MenuDropdown from "@Components/MenuDropdown";
import styles from "./FiltersBadgeGroupDesktop.module.scss";
import FullPageLoader from "@Components/FullPageLoader";
import FiltersBadgeLazyLoader from "./components/FiltersBadgeLazyLoader";
import reorderFilter, {
  getBadgeValue,
  getLocalityOrUniversityTitle,
  hasAnyAmenitiesOptions,
  hasAnyBillsIncludedOptions,
  shouldHideAmenitiesOrBillsPill,
} from "./filtersBadgeHelper";
import wrapperHOC from "@Utils/wrapperHOC";
import useWhitelabel from "hooks/useWhitelabel";
import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";
import useIsTabletPortrait from "hooks/useIsTabletPortrait";
import { useFeatureIsOn, useGrowthBook } from "@growthbook/growthbook-react";
import { GROWTH_BOOK_FEATURE_FLAG_NAMES } from "constants/index";
import useAnimationWithCookie from "hooks/useAnimationWithCookie";
import Chevron from "@Icons/Chevron";
import { useAmberSales } from "hooks/useAmberSales";
import { SmartFilterPill } from "../SmartFilters";
import useTranslationHook from "hooks/useTranslationHook";
import config from "@Config/index";

const ActiveFilterComponent = lazy(() => import("../ActiveFilterComponent/ActiveFilterComponent"));

const HousingTypeChevron = () => (
  <span className={styles.housingTypeChevron}>
    <Chevron />
  </span>
);

type Props = {
  filtersState: any;
  dispatch: any;
  universityData: any;
  localityData: any;
  onFilterClick: any;
  onApplyClick?: any;
  onResetClick?: any;
  activeFilter?: any;
  handleOnClose?: any;
  dynamiceFilterCount?: any;
  isFilterCountLoading?: boolean;
  openFiltersModal?: () => void;
  filterCount?: number;
};

const FiltersBadgeGroupDesktop = ({
  filtersState,
  dispatch,
  universityData,
  localityData,
  onFilterClick,
  onApplyClick,
  onResetClick,
  activeFilter,
  handleOnClose,
  dynamiceFilterCount = null,
  isFilterCountLoading,
  openFiltersModal,
  filterCount = 0,
}: Props) => {
  type mapProps = {
    title: (typeof FILTERS)[number]["name"] | "Sort";
    icon: ({ fill }: { fill?: string | undefined }) => JSX.Element;
    whitelabelTitle: (typeof FILTERS)[number]["whitelabelTitle"] | "Sort by";
    translationKey?: string;
  };

  const { region, inventoryCount, sourceCountry, countryShortName, currencyExchangeRates } =
    useSelector(
      ({ searchPageData, appData }: AppState) => ({
        region: searchPageData.region,
        inventoryCount: searchPageData.searchList?.meta?.count,
        sourceCountry: appData.country,
        countryShortName: searchPageData.region?.location?.country?.short_name,
        currencyExchangeRates: appData.currencyExchangeRates,
      }),
      shallowEqual,
    );
  const isGuarantorInlineFilter = true;
  const showShimmerForFiltersFlag = useFeatureIsOn(
    GROWTH_BOOK_FEATURE_FLAG_NAMES.SHOW_FILTERS_SHIMMER_ANIMATION,
  );
  const { isWhitelabel, isUniversityPartner, whitelabelConfig } = useWhitelabel();
  const { isSaleOfferAvailable } = useAmberSales({ page: "SEARCH" });
  const { t } = useTranslationHook();
  const isTabletPortrait = useIsTabletPortrait();
  let ORDERED_FILTERS = reorderFilter(region, true); // Include Amenities in main filters
  if (isGuarantorInlineFilter) {
    ORDERED_FILTERS = ORDERED_FILTERS.filter((filter: any) => filter?.name !== "Guarantor");
  }

  const shouldShowHousingTypeFilter =
    isWhitelabel &&
    isUniversityPartner &&
    whitelabelConfig?.is_university_properties_section_enabled;

  // extract housing type filter and remove from list for custom positioning
  const housingTypeFilter = ORDERED_FILTERS.find((filter: any) => filter?.name === "Housing Type");
  ORDERED_FILTERS = ORDERED_FILTERS.filter((filter: any) => filter?.name !== "Housing Type");

  const isBillsIncludedActive = !!getFilterActiveStatus("Bills Included", filtersState);
  if (
    shouldHideAmenitiesOrBillsPill({
      isActive: isBillsIncludedActive,
      isFilterCountLoading,
      inventoryCount,
      hasAnyOptions: hasAnyBillsIncludedOptions(dynamiceFilterCount?.data),
      hasFilterCountPayload: !!dynamiceFilterCount?.data,
    })
  ) {
    ORDERED_FILTERS = ORDERED_FILTERS.filter((filter: any) => filter?.name !== "Bills Included");
  }

  const isAmenitiesActive = !!getFilterActiveStatus("Amenities", filtersState);
  if (
    shouldHideAmenitiesOrBillsPill({
      isActive: isAmenitiesActive,
      isFilterCountLoading,
      inventoryCount,
      hasAnyOptions: hasAnyAmenitiesOptions(dynamiceFilterCount?.data),
      hasFilterCountPayload: !!dynamiceFilterCount?.data,
    })
  ) {
    ORDERED_FILTERS = ORDERED_FILTERS.filter((filter: any) => filter?.name !== "Amenities");
  }

  // Show Locality pill only when it's actively applied on desktop (user arrived via locality filter)
  const isLocalityActive = !!getFilterActiveStatus("Locality", filtersState);
  if (HIDE_LOCALITY_FROM_TOP_BAR) {
    ORDERED_FILTERS = ORDERED_FILTERS.filter((filter: any) => filter?.name !== "Locality");
    if (isLocalityActive && LOCALITY_FILTER) {
      ORDERED_FILTERS = [LOCALITY_FILTER, ...ORDERED_FILTERS];
    }
  }

  const shouldAnimate = useAnimationWithCookie(4000, "filter_badge_animation_shown", 30);
  const badgeRef = useRef<HTMLDivElement>(null);
  const growthBook = useGrowthBook();
  const isGrowthBookReady = growthBook?.ready ?? false;
  const smartFiltersFlag = useFeatureIsOn(GROWTH_BOOK_FEATURE_FLAG_NAMES.SMART_FILTERS);
  const isSmartFiltersEnabled = !config.IS_CHINA_APP && smartFiltersFlag;

  const localityOrUniversityTitle = getLocalityOrUniversityTitle(
    countryShortName,
    sourceCountry,
    filtersState,
  );
  const shouldShowAnimation = (title: string, isActive: boolean) =>
    localityOrUniversityTitle === title && shouldAnimate && showShimmerForFiltersFlag && !isActive;
  const renderAnimateBadge = ({
    title,
    whitelabelTitle,
    activeLabel,
    isActive,
    icon,
    translationKey,
  }: {
    title: string;
    whitelabelTitle: string;
    activeLabel: any;
    isActive: boolean;
    icon: any;
    translationKey?: string;
  }) => {
    const isHousingType = title === "Housing Type";
    const iconComponent = isHousingType ? HousingTypeChevron : icon;
    const badgeValue =
      translationKey || getBadgeValue(isWhitelabel, isUniversityPartner, whitelabelTitle, title);
    const showAnimation = shouldShowAnimation(title, isActive);
    const badgeProps = {
      ref: badgeRef,
      value: badgeValue,
      activeLabel,
      isActive: isActive || activeFilter === title,
      onClick: () => onFilterClick(title),
      icon: iconComponent,
      className: formatClassNames(
        styles.badge,
        isHousingType && styles.housingTypeBadge,
        showAnimation && styles.animateBadge,
      ),
      showIconOnRight: isHousingType,
    };

    return <Badge {...badgeProps} />;
  };

  const filtersToRender =
    shouldShowHousingTypeFilter && housingTypeFilter
      ? [housingTypeFilter, SORT_FILTER, ...ORDERED_FILTERS]
      : [SORT_FILTER, ...ORDERED_FILTERS];

  // Add Housing Fest pill after Sort when sale is available
  const finalFiltersToRender =
    isSaleOfferAvailable && HOUSING_FEST_FILTER
      ? (() => {
          const sortIndex = filtersToRender.findIndex((f: any) => f.name === "Sort");
          const result = [...filtersToRender];
          result.splice(sortIndex + 1, 0, HOUSING_FEST_FILTER);
          return result;
        })()
      : filtersToRender;

  return (
    <>
      {/* @ts-ignore */}
      {region?.location?.country?.long_name ? (
        <>
          {!config.IS_CHINA_APP &&
            (!isGrowthBookReady ? (
              <div className={styles.smartFilterSkeleton} />
            ) : (
              isSmartFiltersEnabled && <SmartFilterPill />
            ))}
          {openFiltersModal && (
            <Badge
              value="Filters"
              onClick={openFiltersModal}
              icon={FilterIcon}
              isActive={getFilterActiveStatus("Filters", filtersState)}
              count={filterCount}
              dataTestId="filters-button"
            />
          )}
          {finalFiltersToRender.map(
            ({ title, icon, whitelabelTitle, translationKey }: mapProps, index) => {
              const isActive = getFilterActiveStatus(title as FilterNameType, filtersState);
              const activeLabel =
                isActive &&
                getFilterLabel(
                  title as FilterNameType,
                  filtersState,
                  region,
                  currencyExchangeRates,
                  t,
                );
              const showSeparator = shouldShowHousingTypeFilter && index === 0;

              // Housing Fest is a direct toggle, no dropdown needed
              if (title === "Housing Fest") {
                return (
                  <React.Fragment key={`${title}_${index}`}>
                    {renderAnimateBadge({
                      title,
                      whitelabelTitle: whitelabelTitle || "Housing Fest",
                      activeLabel,
                      isActive,
                      icon,
                      translationKey:
                        translationKey || "searchpage.searchlist.filtersubsection.housingfest",
                    })}
                  </React.Fragment>
                );
              }

              return (
                <React.Fragment key={`${title}_${index}`}>
                  <MenuDropdown
                    addFixed
                    menu={
                      <Suspense fallback={<FullPageLoader height="230px" width="360px" />}>
                        <ActiveFilterComponent
                          filterName={title}
                          filtersState={filtersState}
                          dispatch={dispatch}
                          universityData={universityData}
                          localityData={localityData}
                          onApplyClick={onApplyClick}
                          onResetClick={onResetClick}
                          withResetButton
                          isWhitelabel={isWhitelabel}
                          dynamiceFilterCount={dynamiceFilterCount}
                          showCount
                        />
                      </Suspense>
                    }
                    target={renderAnimateBadge({
                      title,
                      whitelabelTitle,
                      activeLabel,
                      isActive,
                      icon,
                    })}
                    menuContainerClassName={formatClassNames(styles.container, styles.elevatedMenu)}
                    handleOnClose={handleOnClose}
                    dataTestId="search-filters"
                    filterTestId={title}
                    closeOnSelect={isTabletPortrait}
                  />
                  {showSeparator && (
                    <div className={styles.separator} data-testid="housing-type-separator" />
                  )}
                </React.Fragment>
              );
            },
          )}
        </>
      ) : (
        <>
          {Array.from({ length: 5 }, (_, index) => index + 1).map((ele) => (
            <FiltersBadgeLazyLoader key={ele} />
          ))}
        </>
      )}
    </>
  );
};

export default wrapperHOC(FiltersBadgeGroupDesktop, {
  componentName: "FiltersBadgeGroupDesktop-FilterSection-SearchPage",
  showForChina: true,
});
